import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";

const toSafeMillimeters = (value, fallback, min = 15, max = 120) => {
  const size = Number(value);
  if (!Number.isFinite(size)) return fallback;
  return Math.min(Math.max(size, min), max);
};

const LABEL_PRINTER_SIZE_KEY = "pharmacy.label-printer-size";
const LABEL_PRINTER_SETTINGS_VERSION = 4;
const DEFAULT_LABEL_PRINTER_SIZE = { widthMm: 50, heightMm: 25 };
const DEFAULT_LABEL_PRINTER_SETTINGS = {
  ...DEFAULT_LABEL_PRINTER_SIZE,
  qrSizeMm: 23,
  topMarginMm: 1,
  bottomMarginMm: 1,
  leftMarginMm: 1,
  rightMarginMm: 1,
  copies: 1,
};

const toSafeNumber = (value, fallback, min, max) =>
  toSafeMillimeters(value, fallback, min, max);

const sanitizeLabelPrinterSettings = (settings, fallback = DEFAULT_LABEL_PRINTER_SETTINGS) => {
  let widthMm = toSafeNumber(settings?.widthMm, fallback.widthMm, 20, 80);
  let heightMm = toSafeNumber(settings?.heightMm, fallback.heightMm, 15, 120);
  // These rolls are horizontal. A swapped 25 × 50 page prints across extra stickers.
  if (heightMm > widthMm) {
    const swapped = widthMm;
    widthMm = heightMm;
    heightMm = swapped;
  }
  const maxHorizontalMargins = Math.max(0, widthMm - 5);
  const leftMarginMm = Math.min(
    toSafeNumber(settings?.leftMarginMm, fallback.leftMarginMm, 0, 20),
    maxHorizontalMargins,
  );
  const rightMarginMm = Math.min(
    toSafeNumber(settings?.rightMarginMm, fallback.rightMarginMm, 0, 20),
    maxHorizontalMargins - leftMarginMm,
  );
  const maxVerticalMargins = Math.max(0, heightMm - 5);
  const topMarginMm = Math.min(
    toSafeNumber(settings?.topMarginMm, fallback.topMarginMm, 0, 20),
    maxVerticalMargins,
  );
  const bottomMarginMm = Math.min(
    toSafeNumber(settings?.bottomMarginMm, fallback.bottomMarginMm, 0, 20),
    maxVerticalMargins - topMarginMm,
  );

  return {
    widthMm,
    heightMm,
    qrSizeMm: toSafeNumber(settings?.qrSizeMm, fallback.qrSizeMm, 5, 75),
    topMarginMm,
    bottomMarginMm,
    leftMarginMm,
    rightMarginMm,
    copies: toSafeNumber(settings?.copies, fallback.copies, 1, 100),
  };
};

export const getLabelPrinterSettings = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(LABEL_PRINTER_SIZE_KEY));
    if (saved?.version === LABEL_PRINTER_SETTINGS_VERSION) {
      return sanitizeLabelPrinterSettings(saved);
    }

    const standardProfile = {
      ...DEFAULT_LABEL_PRINTER_SETTINGS,
      version: LABEL_PRINTER_SETTINGS_VERSION,
    };
    window.localStorage.setItem(
      LABEL_PRINTER_SIZE_KEY,
      JSON.stringify(standardProfile),
    );
    return DEFAULT_LABEL_PRINTER_SETTINGS;
  } catch {
    // Use defaults when storage is unavailable or invalid.
  }
  return DEFAULT_LABEL_PRINTER_SETTINGS;
};

export const setLabelPrinterSettings = (settings) => {
  const previous = getLabelPrinterSettings();
  const next = sanitizeLabelPrinterSettings(settings, previous);
  try {
    window.localStorage.setItem(
      LABEL_PRINTER_SIZE_KEY,
      JSON.stringify({ ...next, version: LABEL_PRINTER_SETTINGS_VERSION }),
    );
  } catch {
    // Printing still uses the selected settings if storage is unavailable.
  }
  return next;
};

export const getLabelPrinterSize = () => {
  const { widthMm, heightMm } = getLabelPrinterSettings();
  return { widthMm, heightMm };
};

export const setLabelPrinterSize = (widthMm, heightMm) => {
  const settings = setLabelPrinterSettings({ widthMm, heightMm });
  return { widthMm: settings.widthMm, heightMm: settings.heightMm };
};

const mmToPx = (mm) => Math.max(1, Math.round(Number(mm) * 8));

const createCanvas = (widthPx, heightPx) => {
  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, widthPx, heightPx);
  ctx.fillStyle = "#000000";
  return { canvas, ctx };
};

const fitText = (ctx, text, maxWidth) => {
  let display = String(text || "");
  if (!display) return "";
  if (ctx.measureText(display).width <= maxWidth) return display;
  while (display.length > 1 && ctx.measureText(`${display}…`).width > maxWidth) {
    display = display.slice(0, -1);
  }
  return `${display}…`;
};

const drawTextBlock = (ctx, text, x, y, maxWidth, fontPx, { align = "left", weight = "700", maxLines = 2 } = {}) => {
  ctx.fillStyle = "#000000";
  ctx.font = `${weight} ${fontPx}px Arial, Helvetica, sans-serif`;
  ctx.textAlign = align;
  ctx.textBaseline = "top";
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 0;

  const lines = [];
  let current = "";
  words.forEach((word) => {
    const trial = current ? `${current} ${word}` : word;
    if (ctx.measureText(trial).width <= maxWidth || !current) {
      current = trial;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);

  const used = lines.slice(0, maxLines);
  if (lines.length > maxLines) {
    used[maxLines - 1] = fitText(ctx, used[maxLines - 1], maxWidth);
  }
  used.forEach((line, index) => {
    ctx.fillText(fitText(ctx, line, maxWidth), x, y + index * Math.round(fontPx * 1.15));
  });
  return used.length * Math.round(fontPx * 1.15);
};

const buildBarcodeCanvas = (value, targetWidthPx, targetHeightPx, showValue = false) => {
  const output = document.createElement("canvas");
  output.width = targetWidthPx;
  output.height = targetHeightPx;
  const ctx = output.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, targetWidthPx, targetHeightPx);
  try {
    const canvas = document.createElement("canvas");
    const barHeight = showValue
      ? Math.max(16, targetHeightPx - 18)
      : Math.max(16, targetHeightPx);
    JsBarcode(canvas, String(value), {
      format: "CODE128",
      height: barHeight,
      width: 2,
      displayValue: showValue,
      fontSize: 14,
      textMargin: 2,
      margin: 0,
      background: "#ffffff",
      lineColor: "#000000",
    });
    ctx.drawImage(canvas, 0, 0, targetWidthPx, targetHeightPx);
  } catch {
    ctx.fillStyle = "#000000";
    ctx.font = `700 ${Math.max(12, Math.round(targetHeightPx * 0.4))}px Arial, Helvetica, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(value), targetWidthPx / 2, targetHeightPx / 2);
  }
  return output;
};

const buildQrCanvas = async (value, sizePx) => {
  const canvas = document.createElement("canvas");
  await QRCode.toCanvas(canvas, String(value), {
    width: Math.max(32, sizePx),
    margin: 1,
    errorCorrectionLevel: "M",
    color: { dark: "#000000", light: "#ffffff" },
  });
  return canvas;
};

const renderLabelImage = async ({ barcode, name, mode = "both" }, settings) => {
  const code = String(barcode || "").trim();
  if (!code) return "";

  const safeMode = ["barcode", "qr", "both"].includes(mode) ? mode : "both";
  const widthPx = mmToPx(settings.widthMm);
  const heightPx = mmToPx(settings.heightMm);
  const padL = mmToPx(settings.leftMarginMm);
  const padR = mmToPx(settings.rightMarginMm);
  const padT = mmToPx(settings.topMarginMm);
  const padB = mmToPx(settings.bottomMarginMm);
  const innerW = Math.max(8, widthPx - padL - padR);
  const innerH = Math.max(8, heightPx - padT - padB);
  const { canvas, ctx } = createCanvas(widthPx, heightPx);

  if (safeMode === "qr") {
    const qrSize = Math.min(innerW, innerH);
    const qrCanvas = await buildQrCanvas(code, qrSize);
    ctx.drawImage(
      qrCanvas,
      Math.round((widthPx - qrSize) / 2),
      Math.round((heightPx - qrSize) / 2),
      qrSize,
      qrSize,
    );
    return canvas.toDataURL("image/png");
  }

  if (safeMode === "barcode") {
    const barcodeCanvas = buildBarcodeCanvas(code, innerW, innerH, true);
    ctx.drawImage(barcodeCanvas, padL, padT, innerW, innerH);
    return canvas.toDataURL("image/png");
  }

  const title = String(name || code).trim() || code;
  const nameFont = Math.max(10, Math.round(innerH * 0.16));
  const codeFont = Math.max(8, Math.round(innerH * 0.12));
  const gap = Math.max(2, mmToPx(0.6));
  const requestedQr = mmToPx(settings.qrSizeMm);
  const maxQr = Math.min(innerH, Math.round(innerW * 0.42));
  const qrSize = Math.max(24, Math.min(maxQr, requestedQr));
  const textW = Math.max(24, innerW - qrSize - gap);
  const qrCanvas = await buildQrCanvas(code, qrSize);
  ctx.drawImage(qrCanvas, padL + innerW - qrSize, padT + Math.round((innerH - qrSize) / 2), qrSize, qrSize);

  let textY = padT;
  textY += drawTextBlock(ctx, title, padL, textY, textW, nameFont, { maxLines: 2 });
  textY += gap;
  const barcodeH = Math.max(16, innerH - (textY - padT) - codeFont - gap);
  if (barcodeH >= 14) {
    const barcodeCanvas = buildBarcodeCanvas(code, textW, barcodeH);
    ctx.drawImage(barcodeCanvas, padL, textY, textW, barcodeH);
  }
  ctx.font = `600 ${codeFont}px Arial, Helvetica, sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "bottom";
  ctx.fillText(fitText(ctx, code, textW), padL, padT + innerH);
  return canvas.toDataURL("image/png");
};

const printHtmlImages = (images, widthMm, heightMm) => {
  const labelsMarkup = images
    .map((src, index) => `<img class="label" src="${src}" alt="Label ${index + 1}" />`)
    .join("");

  const html = `
    <html>
      <head>
        <title>Medicine labels</title>
        <style>
          @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
          * { margin: 0; padding: 0; }
          html, body {
            width: ${widthMm}mm;
            margin: 0;
            padding: 0;
            background: #ffffff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          img.label {
            display: block;
            width: ${widthMm}mm;
            height: ${heightMm}mm;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          img.label + img.label {
            page-break-before: always;
            break-before: page;
          }
        </style>
      </head>
      <body>
        ${labelsMarkup}
        <script>
          window.addEventListener("load", () => {
            const images = Array.from(document.images);
            Promise.all(
              images.map((image) =>
                image.complete
                  ? Promise.resolve()
                  : new Promise((resolve) => {
                      image.addEventListener("load", resolve, { once: true });
                      image.addEventListener("error", resolve, { once: true });
                    }),
              ),
            ).then(() => {
              window.focus();
              window.print();
            });
          });
        <\/script>
      </body>
    </html>
  `;

  const popup = window.open("", "medicine-label-print");
  if (!popup) return false;
  popup.document.write(html);
  popup.document.close();
  return true;
};

const printPdfImages = (images, widthMm, heightMm) => {
  const landscape = widthMm >= heightMm;
  const pdf = new jsPDF({
    orientation: landscape ? "landscape" : "portrait",
    unit: "mm",
    format: [widthMm, heightMm],
    compress: true,
  });

  images.forEach((src, index) => {
    if (index > 0) {
      pdf.addPage([widthMm, heightMm], landscape ? "landscape" : "portrait");
    }
    pdf.addImage(src, "PNG", 0, 0, widthMm, heightMm, undefined, "FAST");
  });

  pdf.autoPrint();
  const blobUrl = pdf.output("bloburl");
  const popup = window.open(blobUrl, "medicine-label-print");
  if (!popup) return printHtmlImages(images, widthMm, heightMm);
  return true;
};

/**
 * Print labels as individual physical-label pages in one print job.
 * @param {{ barcode: string, name?: string, mode?: 'barcode'|'qr'|'both' }[]} labels
 * @param {{ widthMm?: number, heightMm?: number, qrSizeMm?: number }} options
 */
export const printMedicineLabels = async (
  labels,
  { widthMm, heightMm, qrSizeMm } = {},
) => {
  const printableLabels = (Array.isArray(labels) ? labels : []).filter((label) =>
    Boolean(String(label?.barcode || "").trim()),
  );
  if (!printableLabels.length) return false;

  const configuredSettings = getLabelPrinterSettings();
  const labelSettings = sanitizeLabelPrinterSettings({
    ...configuredSettings,
    widthMm: widthMm ?? configuredSettings.widthMm,
    heightMm: heightMm ?? configuredSettings.heightMm,
    qrSizeMm: qrSizeMm ?? configuredSettings.qrSizeMm,
  });
  const labelImages = (
    await Promise.all(
      printableLabels.map((label) => renderLabelImage(label, labelSettings)),
    )
  ).filter(Boolean);
  if (!labelImages.length) return false;

  return printPdfImages(labelImages, labelSettings.widthMm, labelSettings.heightMm);
};

/**
 * Print copies of one medicine label. Kept for existing single-label callers.
 * @param {{ barcode: string, name?: string, mode?: 'barcode'|'qr'|'both', copies?: number, widthMm?: number, heightMm?: number, qrSizeMm?: number }} options
 */
export const printMedicineLabel = async ({
  barcode,
  name,
  mode = "both",
  copies,
  widthMm,
  heightMm,
  qrSizeMm,
}) => {
  const configuredCopies = getLabelPrinterSettings().copies;
  const labelCount = Math.max(1, Number.parseInt(copies ?? configuredCopies, 10) || 1);
  return printMedicineLabels(
    Array.from({ length: labelCount }, () => ({ barcode, name, mode })),
    { widthMm, heightMm, qrSizeMm },
  );
};

export const previewMedicineLabel = async ({
  barcode,
  name,
  mode = "qr",
  widthMm,
  heightMm,
  qrSizeMm,
} = {}) => {
  const configuredSettings = getLabelPrinterSettings();
  const labelSettings = sanitizeLabelPrinterSettings({
    ...configuredSettings,
    widthMm: widthMm ?? configuredSettings.widthMm,
    heightMm: heightMm ?? configuredSettings.heightMm,
    qrSizeMm: qrSizeMm ?? configuredSettings.qrSizeMm,
  });
  return renderLabelImage(
    { barcode: String(barcode || "PREVIEW").trim(), name, mode },
    labelSettings,
  );
};

export default printMedicineLabel;
